import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import PageHeader from "../../components/common/PageHeader";
import FilterBar from "../../components/common/FilterBar";
import DataTableWrapper from "../../components/common/DataTableWrapper";
import EmptyState from "../../components/common/EmptyState";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useGlobalModals } from "../../contexts/GlobalModalsContext";
import {
  useGenerateSeriesMutation,
  useListSeriesQuery,
  usePauseSeriesMutation,
  useResumeSeriesMutation,
  useArchiveSeriesMutation,
} from "../../features/transfers/seriesApi";
import { useClientSearch, useProviderSearch } from "../../hooks/useMasterdataSearch";
import AsyncAutocompleteWithQuickCreate from "../../components/common/AsyncAutocompleteWithQuickCreate";
import type { Client, Provider } from "../../features/masterdata/types";
import type { SeriesStatus } from "../../features/transfers/types";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const maskToWeekdays = (mask: number) =>
  WEEKDAYS.filter((d, idx) => ((mask >> idx) & 1) === 1);

type SeriesOrdering = "-created_at" | "created_at" | "start_date" | "-start_date";

export default function SeriesListPage() {
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { showError, showConfirm } = useGlobalModals();

  const [filters, setFilters] = usePersistedListFilters("series", {
    status: "" as SeriesStatus | "",
    frequency: "" as "" | "daily" | "weekly",
    client: "",
    defaultProvider: "",
    search: "",
    ordering: "-created_at" as SeriesOrdering,
  });
  const { status, frequency, client, defaultProvider, search, ordering } = filters;
  const debouncedSearch = useDebouncedValue(search);

  const [page, setPage] = useState(1);

  const toggleSort = (field: "start_date" | "created_at") => {
    setFilters((prev) => ({
      ...prev,
      ordering: (prev.ordering === field ? `-${field}` : prev.ordering === `-${field}` ? field : field) as SeriesOrdering,
    }));
  };
  const sortIcon = (field: "start_date" | "created_at") => {
    if (ordering === field) return " ↑";
    if (ordering === `-${field}`) return " ↓";
    return " ⇅";
  };
  const sortAria = (field: "start_date" | "created_at"): "ascending" | "descending" | "none" =>
    ordering === field ? "ascending" : ordering === `-${field}` ? "descending" : "none";

  const [isClientFilterOpen, setClientFilterOpen] = useState(false);
  const [isProviderFilterOpen, setProviderFilterOpen] = useState(false);
  const clientSearch = useClientSearch(client || null, { menuOpen: isClientFilterOpen });
  const providerSearch = useProviderSearch(defaultProvider || null, { menuOpen: isProviderFilterOpen });
  const clientFilterOptions = useMemo(
    () => [{ id: "", name: "All" } as Client, ...clientSearch.options],
    [clientSearch.options]
  );
  const providerFilterOptions = useMemo(
    () => [{ id: "", name: "All" } as Provider, ...providerSearch.options],
    [providerSearch.options]
  );

  useEffect(() => {
    setPage(1);
  }, [status, frequency, client, defaultProvider, debouncedSearch, ordering]);

  const { data, isLoading, isFetching, error, refetch } = useListSeriesQuery({
    page,
    status: status || undefined,
    frequency: frequency || undefined,
    client: client || undefined,
    default_provider: defaultProvider || undefined,
    search: debouncedSearch,
    ordering,
  });

  const rows = data?.results ?? [];

  // Generate modal
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateUntil, setGenerateUntil] = useState("");
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null);

  const [generateSeries, { isLoading: isGenerating }] = useGenerateSeriesMutation();
  const [pauseSeries, { isLoading: isPausing }] = usePauseSeriesMutation();
  const [resumeSeries, { isLoading: isResuming }] = useResumeSeriesMutation();
  const [archiveSeries, { isLoading: isArchiving }] = useArchiveSeriesMutation();

  const openGenerate = (seriesId: string) => {
    setSelectedSeriesId(seriesId);
    setGenerateUntil("");
    setGenerateOpen(true);
  };

  const onGenerate = async () => {
    if (!selectedSeriesId) return;
    try {
      const res = await generateSeries({
        id: selectedSeriesId,
        generate_until: generateUntil || null,
      }).unwrap();

      enqueueSnackbar(`Created ${res.created} transfers`, { variant: "success" });
      setGenerateOpen(false);
    } catch (e: any) {
      enqueueSnackbar(e?.data?.detail || "Failed to generate", { variant: "error" });
    }
  };

  const onPauseResume = async (seriesId: string, next: "paused" | "active") => {
    try {
      if (next === "paused") {
        await pauseSeries({ id: seriesId }).unwrap();
        enqueueSnackbar("Series paused", { variant: "success" });
      } else {
        await resumeSeries({ id: seriesId }).unwrap();
        enqueueSnackbar("Series resumed", { variant: "success" });
      }
    } catch (e: any) {
      enqueueSnackbar(e?.data?.detail || "Failed", { variant: "error" });
    }
  };

  const onArchive = async (seriesId: string) => {
    const confirmed = await showConfirm({
      title: "Archive series?",
      message: "This series will be archived and hidden from the default list.",
      confirmText: "Archive",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await archiveSeries({ id: seriesId }).unwrap();
      enqueueSnackbar("Series archived", { variant: "success" });
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to archive series";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Archive failed", message: typeof msg === "string" ? msg : "Could not archive series.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const showEmpty = !isLoading && !error && rows.length === 0;

  const canPrev = !!data?.previous && page > 1;
  const canNext = !!data?.next;

  return (
    <Box>
      <PageHeader
        title="Series"
        subtitle="Recurring schedules that generate transfers"
        actions={
          <Button variant="contained" onClick={() => nav("/app/series/new")}>
            New Series
          </Button>
        }
      />

      <FilterBar loading={isLoading || isFetching}>
        <TextField
          label="Status"
          select
          value={status}
          onChange={(e) => setFilters({ status: e.target.value as SeriesStatus | "" })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 140 }, width: { xs: "100%", sm: "auto" } }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="paused">Paused</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
        </TextField>
        <TextField
          label="Frequency"
          select
          value={frequency}
          onChange={(e) => setFilters({ frequency: e.target.value as "" | "daily" | "weekly" })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 120 }, width: { xs: "100%", sm: "auto" } }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="daily">Daily</MenuItem>
          <MenuItem value="weekly">Weekly</MenuItem>
        </TextField>
        <Box sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}>
          <AsyncAutocompleteWithQuickCreate<Client>
            label="Client"
            value={client}
            onChange={(id) => setFilters({ client: id ?? "" })}
            options={clientFilterOptions}
            getOptionLabel={(c) => c.name}
            showCreate={false}
            loading={clientSearch.loading}
            onInputChange={(_, v) => clientSearch.setSearchTerm(v)}
            filterOptions={(opts) => opts}
            onOpen={() => setClientFilterOpen(true)}
            onClose={() => setClientFilterOpen(false)}
            placeholder="Type to search…"
          />
        </Box>
        <Box sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}>
          <AsyncAutocompleteWithQuickCreate<Provider>
            label="Default provider"
            value={defaultProvider}
            onChange={(id) => setFilters({ defaultProvider: id ?? "" })}
            options={providerFilterOptions}
            getOptionLabel={(p) => p.name}
            showCreate={false}
            loading={providerSearch.loading}
            onInputChange={(_, v) => providerSearch.setSearchTerm(v)}
            filterOptions={(opts) => opts}
            onOpen={() => setProviderFilterOpen(true)}
            onClose={() => setProviderFilterOpen(false)}
            placeholder="Type to search…"
          />
        </Box>
        <TextField
          label="Search (title)"
          value={search}
          onChange={(e) => setFilters({ search: e.target.value })}
          size="small"
          placeholder="Search…"
          sx={{ minWidth: 0, flex: 1, width: { xs: "100%", sm: "auto" } }}
        />
      </FilterBar>

      {showEmpty ? (
        <EmptyState
          title="No series yet"
          description="Create a recurring program (e.g. Mon/Wed/Fri) and generate transfers."
          actionLabel="Create series"
          onAction={() => nav("/app/series/new")}
        />
      ) : (
        <>
          <DataTableWrapper
            loading={isLoading}
            error={!!error}
            errorMessage="Failed to load series."
            onRetry={() => refetch()}
            minWidth={520}
          >
            <Table sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell aria-sort={sortAria("start_date")}>
                    <Box
                      component="span"
                      onClick={() => toggleSort("start_date")}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Dates{sortIcon("start_date")}
                    </Box>
                  </TableCell>
                  <TableCell>Rule</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r: any) => {
                  const rule =
                    r.frequency === "weekly"
                      ? `weekly • every ${r.interval} • ${maskToWeekdays(r.days_of_week_mask ?? 0).join(", ")}`
                      : `daily • every ${r.interval}`;

                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.title}</TableCell>
                      <TableCell>{r.client_name ?? "—"}</TableCell>
                      <TableCell>{r.default_provider_name ?? "—"}</TableCell>
                      <TableCell>
                        {r.start_date} → {r.end_date}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={rule} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.status} />
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="Generate transfers">
                          <IconButton size="small" onClick={() => openGenerate(r.id)}>
                            <BoltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {r.status === "active" ? (
                          <Tooltip title="Pause">
                            <IconButton
                              size="small"
                              onClick={() => onPauseResume(r.id, "paused")}
                              disabled={isPausing || isResuming}
                            >
                              <PauseCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : r.status === "paused" ? (
                          <Tooltip title="Resume">
                            <IconButton
                              size="small"
                              onClick={() => onPauseResume(r.id, "active")}
                              disabled={isPausing || isResuming}
                            >
                              <PlayCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {(r.status === "active" || r.status === "paused") ? (
                          <Tooltip title="Archive">
                            <IconButton
                              size="small"
                              onClick={() => onArchive(r.id)}
                              disabled={isArchiving}
                              aria-label="Archive series"
                            >
                              <ArchiveOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}

                        <Button size="small" onClick={() => nav(`/app/series/${r.id}/edit`)}>
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </DataTableWrapper>

          {/* paging footer */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <div />
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={isFetching || !canPrev}>
                Prev
              </Button>
              <Button onClick={() => setPage((p) => p + 1)} disabled={isFetching || !canNext}>
                Next
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      {/* Generate modal */}
      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate transfers</DialogTitle>
        <DialogContent>
          <TextField
            label="Generate until"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={generateUntil}
            onChange={(e) => setGenerateUntil(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onGenerate} disabled={!selectedSeriesId || isGenerating}>
            {isGenerating ? "Generating..." : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
